import CodeBlock from '../CodeBlock';

function FnSignature({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-left font-mono text-sm bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-2.5 mb-3 text-gray-300 overflow-x-auto">
      {children}
    </div>
  );
}

function Param({ name, type, desc, def }: { name: string; type: string; desc: string; def?: string }) {
  return (
    <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50 last:border-0">
      <code className="text-sky-400 shrink-0">{name}</code>
      <span className="text-gray-600 shrink-0">{type}</span>
      {def && <span className="text-gray-600 shrink-0">= {def}</span>}
      <span className="text-gray-400">{desc}</span>
    </div>
  );
}

export default function DocsDataModule() {
  return (
    <section data-docs-section="data" className="text-left mb-16">
      <h2 className="text-2xl font-bold text-white mb-2 group">
        <a href="#data" className="hover:text-sky-400 transition-colors">pa.data</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h2>
      <p className="text-gray-400 mb-6">
        Market data access with swappable backends. Default provider is yfinance
        off-platform; on-platform, auto-detects parquet datasets.
      </p>

      {/* list_datasets / list_symbols */}
      <div id="data-list" data-docs-section="data-list" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">list_datasets() / list_symbols()</h3>
        <FnSignature>
          pa.data.list_datasets() → list[dict]
        </FnSignature>
        <FnSignature>
          pa.data.list_symbols(source, interval="1d", category="crypto") → list[str]
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Discover what's available. <code className="text-gray-300">list_datasets()</code> scans
          the filesystem for all installed datasets. <code className="text-gray-300">list_symbols()</code> returns
          unique tickers for a given source and timeframe.
        </p>
        <CodeBlock
          code={`datasets = pa.data.list_datasets()
# [{"id": "crypto-kraken", "source": "kraken", "category": "crypto",
#   "timeframes": ["1m","5m","1h","1d"], "symbol_count": 198}, ...]

symbols = pa.data.list_symbols("kraken", interval="1h")
# ["BTC/USD", "ETH/USD", "SOL/USD", ...]`}
        />
      </div>

      {/* prices() */}
      <div id="data-prices" data-docs-section="data-prices" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">prices()</h3>
        <FnSignature>
          pa.data.prices(symbols, start=None, end=None, interval="1d", cache=True) → DataFrame
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Fetch OHLCV price data. Single symbol returns a flat DataFrame with
          columns <code className="text-gray-300">["open", "high", "low", "close", "volume"]</code>.
          Multiple symbols return a MultiIndex <code className="text-gray-300">(symbol, field)</code>.
        </p>
        <div className="space-y-0 mb-3 text-left">
          <Param name="symbols" type="str | list[str]" desc="Ticker or list of tickers." />
          <Param name="start" type="str | None" desc='Start date (inclusive), e.g. "2022-01-01".' def="None" />
          <Param name="end" type="str | None" desc="End date (exclusive). Defaults to today." def="None" />
          <Param name="interval" type="str" desc='"1d", "1h", "5m", etc.' def='"1d"' />
          <Param name="cache" type="bool" desc="Use local disk cache." def="True" />
        </div>
        <CodeBlock
          code={`prices = pa.data.prices("AAPL", start="2023-01-01")
# columns: open, high, low, close, volume

multi = pa.data.prices(["AAPL", "MSFT"], start="2023-01-01")
# columns: MultiIndex (symbol, field)`}
        />
      </div>

      {/* load() */}
      <div id="data-load" data-docs-section="data-load" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">load()</h3>
        <FnSignature>
          pa.data.load(dataset_id, interval="1d") → DataFrame
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Load an entire dataset as a tall, stacked DataFrame with all symbols.
          Returns columns like <code className="text-gray-300">symbol, timestamp, open, high, low, close, volume</code>.
          Stages parquets to local NVMe on first access and caches in memory for
          the process lifetime.
        </p>
        <div className="space-y-0 mb-3 text-left">
          <Param name="dataset_id" type="str" desc='"category-source" format, e.g. "crypto-kraken".' />
          <Param name="interval" type="str" desc='"1d", "1h", "5m", "15m", "30m", "4h", "12h", "1wk".' def='"1d"' />
        </div>
        <CodeBlock
          code={`df = pa.data.load("crypto-kraken", interval="1h")
# → stacked DataFrame [symbol, timestamp, open, high, low, close, volume, trades]

btc = df[df["symbol"] == "BTC/USD"]`}
        />
      </div>

      {/* returns / cumulative_returns */}
      <div id="data-returns" data-docs-section="data-returns" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">returns() / cumulative_returns()</h3>
        <FnSignature>
          pa.data.returns(prices_df, method="simple") → DataFrame
        </FnSignature>
        <FnSignature>
          pa.data.cumulative_returns(returns_df) → DataFrame
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Pure transforms over price DataFrames. <code className="text-gray-300">method</code> can
          be <code className="text-gray-300">"simple"</code> (arithmetic) or <code className="text-gray-300">"log"</code>.
          Cumulative returns use compounding: <code className="text-gray-300">(1 + r).cumprod() - 1</code>.
        </p>
        <CodeBlock
          code={`ret = pa.data.returns(prices)          # simple returns
log_ret = pa.data.returns(prices, method="log")
cum = pa.data.cumulative_returns(ret)  # cumulative wealth index`}
        />
      </div>

      {/* from_csv */}
      <div id="data-from-csv" data-docs-section="data-from-csv" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">from_csv()</h3>
        <FnSignature>
          pa.data.from_csv(path) → DataFrame
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Load prices from a local CSV file. Auto-normalizes column names to
          lowercase OHLCV and parses the index as DatetimeIndex. Expects at
          least one recognizable OHLCV column.
        </p>
        <CodeBlock
          code={`prices = pa.data.from_csv("my_data.csv")
# columns normalized to: open, high, low, close, volume`}
        />
      </div>

      {/* configure / clear_cache */}
      <div id="data-configure" data-docs-section="data-configure" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">configure() / clear_cache()</h3>
        <FnSignature>
          pa.data.configure(provider) → None
        </FnSignature>
        <FnSignature>
          pa.data.clear_cache() → int
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Swap the global data provider at runtime or clear the local disk cache.
          Any object satisfying the <code className="text-gray-300">DataProvider</code> protocol
          (a <code className="text-gray-300">name</code> property and a <code className="text-gray-300">fetch_prices</code> method)
          can be plugged in.
        </p>
        <CodeBlock
          code={`from propalgos.data import CryptoParquetProvider

# Switch to crypto parquet provider
pa.data.configure(CryptoParquetProvider("kraken"))

# Clear all cached data files
removed = pa.data.clear_cache()
print(f"Removed {removed} cached files")`}
        />
      </div>

      {/* DataProvider protocol */}
      <div id="data-providers" data-docs-section="data-providers" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">Providers</h3>
        <p className="text-gray-400 text-sm mb-3">
          Any class that implements the <code className="text-gray-300">DataProvider</code> protocol
          can be passed to <code className="text-gray-300">configure()</code>. Built-in providers:
        </p>
        <div className="overflow-x-auto">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Provider</th>
                <th className="py-2 pr-4 text-gray-400 font-medium">Backend</th>
                <th className="py-2 text-gray-400 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">YFinanceProvider</td>
                <td className="py-2 pr-4">yfinance</td>
                <td className="py-2 text-gray-500">Default off-platform. Included in core install</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">CryptoParquetProvider</td>
                <td className="py-2 pr-4">Local parquet</td>
                <td className="py-2 text-gray-500">Single-exchange crypto data from FUSE-mounted parquets</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-sky-400">CryptoCompositeProvider</td>
                <td className="py-2 pr-4">Multi-exchange parquet</td>
                <td className="py-2 text-gray-500">Falls through multiple exchanges in priority order</td>
              </tr>
            </tbody>
          </table>
        </div>
        <CodeBlock
          code={`from propalgos.data import DataProvider
import pandas as pd

class MyProvider:
    @property
    def name(self) -> str:
        return "my-provider"

    def fetch_prices(self, symbols, start, end, interval) -> pd.DataFrame:
        # Your custom data fetching logic here
        ...

pa.data.configure(MyProvider())`}
        />
      </div>
    </section>
  );
}
